import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';

const priorityOrder = [
  'เบอร์โทรศัพท์มือถือ',
  'เบอร์โทรศัพท์ที่ทำงาน',
  'เบอร์โทรศัพท์บ้าน',
  'เบอร์โทรศัพท์อื่นๆ'
];

const sortPersonNames = (names) =>
  names.sort((a, b) => priorityOrder.indexOf(a) - priorityOrder.indexOf(b));

const mapTypeToKey = (type) => {
  switch (type) {
    case 'เบอร์โทรศัพท์มือถือ': return 'mobile';
    case 'เบอร์โทรศัพท์บ้าน': return 'phone_home';
    case 'เบอร์โทรศัพท์ที่ทำงาน': return 'phone_office';
    case 'เบอร์โทรศัพท์อื่นๆ': return 'phone_other';
    default: return 'error';
  }
};

export default function MultipleSelectPhone({ onInputChange }) {
  const [personName, setPersonName] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [open, setOpen] = useState(false); // ✅ คุมเมนูเอง

  // ถ้าต้องการยิงค่ากลับให้พาเรนต์
  useEffect(() => {
    onInputChange?.(inputValues);
  }, [inputValues, onInputChange]);

  const handleChange = (event, newValue, reason) => {
    const sortedNames = sortPersonNames(newValue);
    setPersonName(sortedNames);

    // สร้าง/คงค่า input ของประเภทที่เลือกไว้
    const updated = { ...inputValues };
    sortedNames.forEach((type) => {
      const key = mapTypeToKey(type);
      if (!(key in updated)) {
        // บ้าน/ที่ทำงาน = 2 ช่อง, อื่นๆ = 1 ช่อง
        updated[key] =
          (type === 'เบอร์โทรศัพท์บ้าน' || type === 'เบอร์โทรศัพท์ที่ทำงาน')
            ? ['', '']
            : '';
      }
    });
    // ลบ key ที่ไม่ถูกเลือกแล้ว
    Object.keys(updated).forEach((key) => {
      const type = priorityOrder.find((t) => mapTypeToKey(t) === key);
      if (!sortedNames.includes(type)) delete updated[key];
    });

    setInputValues(updated);

    // ✅ ปิดเมนูทันทีเมื่อเลือก
    if (reason === 'selectOption') setOpen(false);
  };

  const validatePhoneNumber = (value, type) => {
    const onlyDigits = /^\d*$/;
    if (!onlyDigits.test(value)) return false;
  
    if ((type === 'เบอร์โทรศัพท์มือถือ' || type === 'เบอร์โทรศัพท์อื่นๆ') && value.length > 10) {
      return false; // เกิน 10 ค่อยไม่ให้พิมพ์
    }
    return true;
  };

  const getMaxLenByType = (type, index) => {
    if (type === 'เบอร์โทรศัพท์บ้าน' || type === 'เบอร์โทรศัพท์ที่ทำงาน') {
      return index === 1 ? 5 : 9; // ช่องหลัก 9 หลัก, ช่อง "ต่อ" 0–5 หลัก (ปรับได้)
    }
    // มือถือ/อื่นๆ
    return 10;
  };
  
  const handleInputChange = (type, index, event) => {
    const raw = event.target.value ?? '';
    const maxLen = getMaxLenByType(type, index);
  
    // เก็บเฉพาะตัวเลข และจำกัดความยาวทันที
    const digits = raw.replace(/\D/g, '').slice(0, maxLen);
  
    const updated = { ...inputValues };
    const key = mapTypeToKey(type);
  
    if (type === 'เบอร์โทรศัพท์บ้าน' || type === 'เบอร์โทรศัพท์ที่ทำงาน') {
      const current = Array.isArray(updated[key]) ? [...updated[key]] : ['', ''];
      current[index] = digits;
      updated[key] = current;
    } else {
      updated[key] = digits;
    }
  
    setInputValues(updated);
  };
  

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} justifyContent="flex-start" sx={{ mb: 1 }}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            sx={{ borderRadius: '16px!important' }}
            multiple
            options={priorityOrder}
            value={personName}
            // filterSelectedOptions           // ✅ ซ่อนรายการที่ถูกเลือกแล้ว
            open={open}                     // ✅ คุมเมนูเปิด/ปิดเอง
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            onChange={handleChange}         // ✅ reason จะถูกส่งเข้ามาในพารามิเตอร์ที่ 3
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip key={option} label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                // label="ประเภท"
                placeholder="เลือกประเภทเบอร์โทรศัพท์"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px' } }}
              />
            )}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {personName.map((type) => {
          const key = mapTypeToKey(type);
          return (
            <Box key={type} sx={{ mt: 2, width: '100%' }}>
              <InputLabel sx={{ fontFamily: '"Prompt-Thin", sans-serif !important', color: 'black' }}>
                {type}
              </InputLabel>

              {type === 'เบอร์โทรศัพท์บ้าน' || type === 'เบอร์โทรศัพท์ที่ทำงาน' ? (
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <OutlinedInput
                      inputProps={{
                        maxLength: 9,        // ✅ ความยาวหลักตามที่คุณตั้งใจ
                        inputMode: 'numeric',
                      }}
                      value={inputValues[key]?.[0] || ''}
                      onChange={(e) => handleInputChange(type, 0, e)}
                      fullWidth
                      placeholder={`กรอก ${type}`}
                      sx={{ borderRadius: '16px', mt: 1, height: '40px' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <OutlinedInput
                      inputProps={{
                        maxLength: 3,        // ✅ ต่อภายใน (extension)
                        inputMode: 'numeric',
                      }}
                      value={inputValues[key]?.[1] || ''}
                      onChange={(e) => handleInputChange(type, 1, e)}
                      fullWidth
                      placeholder="ต่อ"
                      sx={{ borderRadius: '16px', mt: 1, height: '40px' }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <OutlinedInput
                  inputProps={{
                    maxLength: 10,       // ✅ มือถือ/อื่นๆ = 10 หลัก
                    inputMode: 'numeric',
                  }}
                  value={inputValues[key] || ''}
                  onChange={(e) => handleInputChange(type, null, e)}
                  fullWidth
                  placeholder={`กรอก ${type}`}
                  sx={{ borderRadius: '16px', mt: 1, height: '40px' }}
                />
              )}
            </Box>
          );
        })}
      </Grid>
    </Box>
  );
}
